/* ═══════════════════════════════════════════════════════════════
   ALTERLY — Sharpen Node
   Convolution-based high-frequency detail enhancement on GPU.
   ═══════════════════════════════════════════════════════════════ */

import type { NodeDefinition } from '../types';
import { GLSL_HEADER } from '../../rendering/effects/glslCommon';
import { createShaderProcessor } from '../../rendering/effects/shaderProcessor';

const SHARPEN_SHADER = `${GLSL_HEADER}
uniform sampler2D u_image;
uniform vec2 u_resolution;
uniform float u_amount;

void main() {
  vec2 step = 1.0 / u_resolution;
  
  vec4 center = texture(u_image, v_uv);
  vec4 top    = texture(u_image, v_uv + vec2(0.0, -step.y));
  vec4 bottom = texture(u_image, v_uv + vec2(0.0, step.y));
  vec4 left   = texture(u_image, v_uv + vec2(-step.x, 0.0));
  vec4 right  = texture(u_image, v_uv + vec2(step.x, 0.0));
  
  // Laplacian high-pass
  vec4 laplacian = 4.0 * center - (top + bottom + left + right);
  vec4 sharpened = center + laplacian * u_amount;
  
  fragColor = vec4(clamp(sharpened.rgb, 0.0, 1.0), center.a);
}
`;

export const sharpenNode: NodeDefinition = {
  type: 'filter.sharpen',
  category: 'Filter',
  label: 'Sharpen',
  description: 'Enhances edges and high-frequency textural clarity',
  keywords: ['sharpen', 'crisp', 'clarity', 'edge', 'details', 'enhance'],
  targetBackend: 'webgl2',
  inputs: [
    { id: 'image', name: 'Source', type: 'image' },
  ],
  outputs: [
    { id: 'output', name: 'Sharpened', type: 'image' },
  ],
  params: [
    {
      id: 'amount',
      name: 'Sharpen Amount',
      type: 'slider',
      defaultValue: 1.0,
      min: 0,
      max: 5,
      step: 0.1,
      precision: 1,
    },
  ],
  processor: createShaderProcessor({
    fragmentShader: SHARPEN_SHADER,
    getUniforms: (inputs, params, dimensions) => ({
      u_image: inputs.image as unknown as import('../../rendering/types').TextureHandle,
      u_resolution: [dimensions.width, dimensions.height],
      u_amount: Number(params.amount ?? 1.0),
    }),
  }),
};
